import articleIndex from '../../content/articles-index.json';
import {
  ARTICLES_URL,
  COMPANY_HOME_URL,
  COMPANY_MARKDOWN_URL,
  MAIA_HOME_URL,
  WHATSAPP_LANDING_URL,
} from '../../support/publicUrls';

export const HOME_URL = COMPANY_HOME_URL;
export const HOME_MARKDOWN_URL = COMPANY_MARKDOWN_URL;
export const LAST_MODIFIED = '2026-11-02';
export const LAST_MODIFIED_LABEL = '2 de novembro de 2026';

export const CONTACT_URL =
  'https://wa.me/5521971792112?text=Ol%C3%A1%2C%20quero%20conhecer%20a%20plataforma%20da%20MAIA.';

export const SEO = {
  title: 'MAIA | Plataforma de IA para criar agentes',
  description:
    'A MAIA é uma plataforma de inteligência artificial para criar agentes que conversam com seu público, usam seu conhecimento e operam em canais como WhatsApp.',
  applicationDescription:
    'Uma plataforma de IA para criar, configurar, publicar e acompanhar agentes de inteligência artificial que conversam com pessoas, inclusive no WhatsApp.',
  canonical: HOME_URL,
};

export const HERO = {
  eyebrow: 'Tecnologia de inteligência artificial para conversas',
  headline: 'Crie e opere agentes de inteligência artificial com a MAIA',
  body:
    'Use a plataforma de IA da MAIA para criar agentes que conversam com seu público, usam seu conhecimento e atendem em canais como WhatsApp — inclusive como agente de IA para WhatsApp.',
};

export const INTRODUCTION = {
  eyebrow: 'Plataforma de IA da MAIA',
  headline: 'Crie agentes de IA para WhatsApp e outros canais no mesmo painel',
  body:
    'A MAIA reúne as ferramentas para configurar um agente que conversa com seu público. Você define como ele responde, o que ele sabe, quais tarefas executa e em quais canais atende — com destaque para o WhatsApp.',
};

export const STEPS_SECTION = {
  eyebrow: 'Como funciona?',
  headline: 'Do primeiro agente à publicação em três passos',
  body: 'A configuração é feita pelo painel e pode evoluir conforme a operação cresce.',
};

export const STEPS = [
  {
    number: '01',
    title: 'Crie o agente principal',
    description:
      'Escolha o nome, o tom de voz e o modelo de inteligência artificial (IA) usado nas conversas.',
  },
  {
    number: '02',
    title: 'Adicione especialistas e conhecimento',
    description:
      'Crie subagentes para tarefas específicas e conecte documentos que devem orientar as respostas.',
  },
  {
    number: '03',
    title: 'Publique nos seus canais',
    description:
      'Publique um agente de IA para WhatsApp e acompanhe as conversas pelo mesmo painel.',
  },
];

export const SHOWCASE_SECTION = {
  eyebrow: 'Por dentro da plataforma',
  headline: 'Veja como cada parte do agente funciona',
  body:
    'Configure comportamentos, conecte seus materiais e integre serviços externos pelo mesmo painel.',
};

export const SHOWCASES = [
  {
    id: 'behaviors',
    eyebrow: 'Comportamentos',
    title: 'Defina o que o agente faz em cada momento da conversa',
    description:
      'Organize funções especializadas para conhecer o contato, executar tarefas, revisar respostas e chamar uma pessoa quando necessário.',
    highlights: [
      'Qualificação do contato',
      'Habilidades e regras de segurança',
      'Escalonamento para atendimento humano',
    ],
    image: '/images/home/comportamentos-agente.webp',
    imageAlt:
      'Fluxo de comportamentos da plataforma da MAIA com qualificação, habilidade, segurança e escalonamento para atendimento humano.',
  },
  {
    id: 'knowledge',
    eyebrow: 'Base de conhecimento',
    title: 'Use o conteúdo da sua operação nas respostas',
    description:
      'Importe seus materiais e acompanhe o processamento até que estejam prontos para orientar o agente com contexto e evidências.',
    highlights: [
      'Google Drive, links, Amazon S3 e Contentful',
      'Acompanhamento da indexação',
      'Conteúdo usado como contexto ou evidência',
    ],
    image: '/images/home/base-conhecimento.webp',
    imageAlt:
      'Cadastro de uma base de conhecimento na plataforma da MAIA a partir de Google Drive, link, Amazon S3 ou Contentful.',
  },
  {
    id: 'integrations',
    eyebrow: 'Integrações MCP',
    title: 'Conecte o agente aos serviços que sua equipe já usa',
    description:
      'Cadastre servidores MCP para que as habilidades consultem informações e executem ações em sistemas externos com controle de acesso.',
    highlights: [
      'Conexões HTTP e Streamable HTTP',
      'Autenticação por token',
      'Servidores ativos ou inativos por integração',
    ],
    image: '/images/home/integracoes-mcp.webp',
    imageAlt:
      'Servidores MCP conectando uma habilidade da plataforma da MAIA a serviços externos de agenda, estoque e financeiro.',
  },
];

export const TRUST_SECTION = {
  eyebrow: 'Clientes e parceiros',
  headline: 'Quem já opera com a MAIA e as tecnologias que sustentam a plataforma',
  body:
    'A MAIA atende operações de conversa em produção e se apoia em tecnologias e parcerias de canais e modelos de inteligência artificial.',
};

export const CLIENTS = [
  {
    id: 'blu',
    name: 'BLU',
    description:
      'Operação de atendimento que usa a plataforma da MAIA para conversar com o público.',
    image: '/images/home/logo-blu.webp',
    imageAlt: 'Logo da BLU, cliente da MAIA na operação de atendimento com agentes de IA.',
    imageWidth: 240,
    imageHeight: 240,
  },
  {
    id: 'botmaker',
    name: 'Botmaker',
    description:
      'Empresa de conversas que opera agentes de inteligência artificial com a MAIA.',
    image: '/images/home/logo-botmaker.webp',
    imageAlt: 'Logo da Botmaker, cliente da MAIA em operações de conversa com IA.',
    imageWidth: 512,
    imageHeight: 440,
  },
];

export const PARTNERS = [
  {
    id: 'meta',
    name: 'Meta',
    description:
      'WhatsApp Business Platform (Cloud API oficial da Meta) para publicar agentes de IA no WhatsApp.',
    image: '/images/home/logo-facebook.svg',
    imageAlt: 'Logo da Meta, usada pela MAIA na WhatsApp Business Platform.',
    imageWidth: 36,
    imageHeight: 36,
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    description:
      'Modelos de inteligência artificial disponíveis para os agentes da plataforma.',
    image: '/images/home/logo-anthropic.png',
    imageAlt: 'Logo da Anthropic, tecnologia de modelos de IA usada na plataforma da MAIA.',
    imageWidth: 512,
    imageHeight: 512,
  },
  {
    id: 'openai',
    name: 'OpenAI',
    description:
      'Modelos de inteligência artificial disponíveis para os agentes da plataforma.',
    image: '/images/home/logo-openai.webp',
    imageAlt: 'Logo da OpenAI, tecnologia de modelos de IA usada na plataforma da MAIA.',
    imageWidth: 505,
    imageHeight: 512,
  },
];

export const CLIENTS_GROUP = {
  eyebrow: 'Clientes',
  headline: 'Operações que já usam a MAIA',
};

export const PARTNERS_GROUP = {
  eyebrow: 'Tecnologias e parcerias',
  headline: 'Canais e modelos que sustentam os agentes',
};

export const FEATURES_SECTION = {
  eyebrow: 'Recursos',
  headline: 'O que você controla na plataforma',
  body:
    'Cada parte do seu sistema de IA pode ser configurada e acompanhada sem espalhar a operação por várias ferramentas.',
};

export const FEATURES = [
  {
    id: 'agents',
    title: 'Agentes',
    description:
      'Defina identidade, tom de voz, modelo de inteligência artificial e regras gerais para cada agente.',
  },
  {
    id: 'subagents',
    title: 'Subagentes',
    description:
      'Separe tarefas como receber novos contatos, executar ações e encaminhar conversas para pessoas.',
  },
  {
    id: 'knowledge',
    title: 'Bases de conhecimento',
    description:
      'Conecte materiais próprios para que as respostas usem informações da sua operação.',
  },
  {
    id: 'channels',
    title: 'Canais',
    description:
      'Publique o agente em canais de conversa, como WhatsApp, e gerencie as conexões pelo painel.',
  },
];

export const AUDIENCE = {
  eyebrow: 'Para quem é?',
  headline: 'Para equipes que precisam colocar inteligência artificial em operação',
  body:
    'A plataforma da MAIA atende quem precisa configurar, publicar e acompanhar sistemas de IA que conversam com pessoas, com regras, conhecimento e tarefas próprias. Ela não é um agente pronto para o consumidor final.',
};

export const FAQ_SECTION = {
  eyebrow: 'Dúvidas frequentes',
  headline: 'Perguntas sobre a plataforma',
};

export const FAQS = [
  {
    question: 'Qual é a ferramenta de criação de agentes da MAIA?',
    answer:
      'Ela se chama Agent Forge e permite criar, configurar, publicar e acompanhar agentes de inteligência artificial usados em conversas.',
  },
  {
    question: 'O que é uma plataforma de IA?',
    answer:
      'É um sistema que reúne as ferramentas para criar e operar soluções de inteligência artificial. Na MAIA, você configura agentes, conecta conhecimento e publica as conversas nos canais da sua operação.',
  },
  {
    question: 'Como usar IA para conversar com meu público?',
    answer:
      'Você configura um agente com o tom, as regras e o conhecimento da sua operação. Ele funciona como uma inteligência artificial para conversar com cada pessoa e encaminhar tarefas quando necessário.',
  },
  {
    question: 'Preciso programar para criar um agente?',
    answer:
      'As principais configurações são feitas pelo painel. Integrações ou operações mais específicas podem exigir apoio técnico.',
  },
  {
    question: 'O que são subagentes?',
    answer:
      'São partes especializadas do agente principal. Cada uma pode cuidar de uma tarefa, como receber um novo contato, consultar conhecimento ou encaminhar uma conversa.',
  },
  {
    question: 'Posso usar meu próprio conteúdo nas respostas?',
    answer:
      'Sim. A plataforma permite conectar bases de conhecimento para orientar o agente com materiais da sua operação.',
  },
  {
    question: 'Em quais canais o agente pode conversar?',
    answer:
      'A plataforma permite publicar agentes em canais de conversa. O WhatsApp é o canal principal para um agente de IA conversar com seus contatos; outros canais entram conforme a operação.',
  },
  {
    question: 'Qual é a relação entre a MAIA e o Agent Forge?',
    answer:
      'A MAIA é a empresa. Agent Forge é o nome da ferramenta usada para criar e operar agentes de IA. A mentoria digital é uma oferta específica da MAIA para quem vende conhecimento.',
  },
];

export const CLOSING = {
  headline: 'Comece a criar seu primeiro agente com a MAIA',
  body:
    'Crie uma conta para conhecer o painel e reunir sua plataforma de IA, agentes, conhecimento e canais em um só lugar. Se preferir, fale com a equipe no WhatsApp.',
  primaryCta: 'Criar conta',
  secondaryCta: 'Falar no WhatsApp',
};

export const RELATED_LINKS = [
  {
    href: WHATSAPP_LANDING_URL,
    label: 'Agente de IA para WhatsApp',
    description: 'Atenda, qualifique e execute tarefas onde as conversas já acontecem.',
  },
  {
    href: MAIA_HOME_URL,
    label: 'Mentoria digital no WhatsApp',
    description: 'Transforme cursos, ebooks e mentorias em uma assistente digital.',
  },
  {
    href: ARTICLES_URL,
    label: 'Artigos sobre agentes de IA e WhatsApp',
    description: 'Guias e boas práticas para criar, publicar e operar agentes.',
  },
];

export const RECENT_ARTICLES_SECTION = {
  eyebrow: 'Conteúdo',
  headline: 'Artigos recentes sobre agentes de IA e WhatsApp',
  body: 'Leia guias práticos e depois volte para criar seu agente na plataforma.',
  allLabel: 'Ver todos os artigos',
  allHref: ARTICLES_URL,
};

export const RECENT_ARTICLES = articleIndex.slice(0, 3).map((article) => ({
  href: `${ARTICLES_URL}/${article.slug}`,
  title: article.title,
  description: article.description,
  date: article.date,
  category: article.category,
}));
